import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type ColumnPinningState,
  type ExpandedState,
  type OnChangeFn,
  type VisibilityState,
} from "@tanstack/react-table"
import { ArrowDown, ArrowUp, ArrowUpDown, Pin, PinOff } from "lucide-react"
import { useEffect, useMemo, useState, type CSSProperties } from "react"
import { SIDE_PEEK_CONTENT_INSET_X } from "@/components/ui/side-peek"
import { cn } from "@/lib/utils"

/**
 * Единый табличный каркас платформы (журнал, ставки биллинга, будущие списки):
 * sticky-шапка, закрепление колонок с persistence в localStorage, сортировка
 * с управляемым снаружи состоянием, плотность h-9 и общий стайлгайд ячеек.
 *
 * Скролл-контейнер — родительский main (без вложенного overflow-x), иначе
 * sticky th/колонки не прилипают. Таблица целиком на сплошном bg-ui-bg —
 * текст не ложится на градиент шелла, а sticky-ячейки ничем не выделяются.
 *
 * Таблица всегда занимает минимум всю ширину скролл-контейнера: при узком
 * наборе колонок лишнее место разбирают незакреплённые data-колонки
 * (авто-раскладка), закреплённые держат свою фиксированную ширину по краям —
 * линии строк в любом случае доходят до края экрана. Если закреплены все
 * колонки, браузер растягивает и их, и sticky-смещения перестают совпадать
 * с рендером (давний путь, не фича).
 */

export type DataTableSortState = { id: string; desc: boolean } | null

export type DataTableLabels = {
  pinColumn: string
  unpinColumn: string
}

export type DataTableProps<T> = {
  data: T[]
  columns: ColumnDef<T>[]
  getRowId: (row: T) => string
  getSubRows?: (row: T) => T[]
  columnVisibility: VisibilityState
  expanded?: ExpandedState
  onExpandedChange?: OnChangeFn<ExpandedState>
  /** Структурные колонки по краям (чекбокс, действия): всегда закреплены, без сортировки и пинов. */
  staticLeftIds?: readonly string[]
  staticRightIds?: readonly string[]
  /** localStorage-ключ пользовательских пинов; без него закрепление недоступно. */
  pinStorageKey?: string
  /** Закреплено из коробки; явное откреплённое состояние пользователя ("[]") уважаем. */
  defaultPinnedLeft?: readonly string[]
  /** Отображаемая сортировка; сам порядок строк — ответственность вызывающего (клиент или сервер). */
  sort?: DataTableSortState
  onSortToggle?: (columnId: string) => void
  /** Какие колонки сортируются; по умолчанию — все данные-колонки (при наличии onSortToggle). */
  sortableIds?: ReadonlySet<string>
  rightAlignedIds?: ReadonlySet<string>
  columnClassName?: (columnId: string) => string | false | undefined
  rowClassName?: (row: T) => string | false | undefined
  /** Подкраска закреплённых ячеек строки (::before-оверлей поверх bg-ui-bg): ховер, выделение, статусные тона. */
  pinnedCellTint?: (row: T) => string | false | undefined
  onRowClick?: (row: T) => void
  labels: DataTableLabels
  emptyLabel: string
  /** Отступ первой колонки от края — для таблиц без структурной колонки-гаттера слева. */
  gutterLeft?: boolean
  /** Симметричный отступ последней колонки — для таблиц без структурной колонки действий справа. */
  gutterRight?: boolean
  minWidthClassName?: string
  className?: string
}

export const DATA_TABLE_TH_CLASS = "sticky top-0 z-10 bg-ui-bg px-3 font-medium whitespace-nowrap text-ui-text-muted"
export const DATA_TABLE_TD_CLASS = "h-9 px-3 py-0 align-middle whitespace-nowrap"

/** Неактивная стрелка видна только по ховеру шапки — место резервируется, чтобы колонки не дёргались. */
export function SortIcon({ active, desc }: { active: boolean; desc: boolean }) {
  if (!active)
    return (
      <ArrowUpDown className="h-3 w-3 shrink-0 text-ui-text-faint opacity-0 transition-opacity group-hover/head:opacity-100" />
    )
  if (desc) return <ArrowDown className="h-3 w-3 shrink-0 text-ui-accent" />
  return <ArrowUp className="h-3 w-3 shrink-0 text-ui-accent" />
}

function loadPinnedLeft(storageKey: string, allowed: readonly string[], fallback: readonly string[]): string[] {
  if (typeof window === "undefined") return [...fallback]
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return [...fallback]
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return [...fallback]
    return parsed.filter((id): id is string => typeof id === "string" && allowed.includes(id))
  } catch {
    return [...fallback]
  }
}

function hasTextSelection(): boolean {
  return (window.getSelection()?.toString() ?? "") !== ""
}

function pinStyle<T>(column: Column<T, unknown>): CSSProperties | undefined {
  const pinned = column.getIsPinned()
  if (!pinned) return undefined
  const size = column.getSize()
  const base = { width: size, minWidth: size, maxWidth: size }
  if (pinned === "left") return { ...base, left: column.getStart("left") }
  return { ...base, right: column.getAfter("right") }
}

function pinnedEdgeClass<T>(column: Column<T, unknown>): string | false {
  const pinned = column.getIsPinned()
  if (pinned === "left" && column.getIsLastColumn("left")) return "border-r border-ui-border"
  if (pinned === "right" && column.getIsFirstColumn("right")) return "border-l border-ui-border"
  return false
}

export function DataTable<T>({
  data,
  columns,
  getRowId,
  getSubRows,
  columnVisibility,
  expanded,
  onExpandedChange,
  staticLeftIds = [],
  staticRightIds = [],
  pinStorageKey,
  defaultPinnedLeft = [],
  sort = null,
  onSortToggle,
  sortableIds,
  rightAlignedIds,
  columnClassName,
  rowClassName,
  pinnedCellTint,
  onRowClick,
  labels,
  emptyLabel,
  gutterLeft = false,
  gutterRight = false,
  minWidthClassName = "min-w-[56rem]",
  className,
}: DataTableProps<T>) {
  const staticIds = useMemo(() => new Set([...staticLeftIds, ...staticRightIds]), [staticLeftIds, staticRightIds])
  const dataColumnIds = useMemo(
    () => columns.map((c) => c.id as string).filter((id) => id && !staticIds.has(id)),
    [columns, staticIds],
  )

  const [pinnedLeft, setPinnedLeft] = useState<string[]>(() =>
    pinStorageKey ? loadPinnedLeft(pinStorageKey, dataColumnIds, defaultPinnedLeft) : [...defaultPinnedLeft],
  )

  useEffect(() => {
    if (!pinStorageKey) return
    try {
      localStorage.setItem(pinStorageKey, JSON.stringify(pinnedLeft))
    } catch {
      // ignore quota / private mode
    }
  }, [pinStorageKey, pinnedLeft])

  const columnPinning = useMemo<ColumnPinningState>(
    () => ({
      left: [...staticLeftIds, ...dataColumnIds.filter((id) => pinnedLeft.includes(id))],
      right: [...staticRightIds],
    }),
    [staticLeftIds, staticRightIds, dataColumnIds, pinnedLeft],
  )

  const table = useReactTable({
    data,
    columns,
    state: { expanded: expanded ?? {}, columnVisibility, columnPinning },
    onExpandedChange,
    getRowId,
    getSubRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  })

  if (data.length === 0) {
    return (
      <div className={cn(SIDE_PEEK_CONTENT_INSET_X, "py-12 text-sm text-ui-text-muted", className)}>{emptyLabel}</div>
    )
  }

  const togglePin = (id: string) => {
    setPinnedLeft((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]))
  }

  /** Закреплённые ячейки перекрывают контент при скролле: плотный фон + тон строки поверх через ::before. */
  const pinnedTdClass = (column: Column<T, unknown>, row: T): string | false => {
    if (!column.getIsPinned()) return false
    return cn(
      "sticky z-[1] overflow-hidden bg-ui-bg",
      "before:pointer-events-none before:absolute before:inset-0 before:z-[-1] before:content-['']",
      pinnedCellTint?.(row),
      pinnedEdgeClass(column),
    )
  }

  return (
    <div className={cn("w-max min-w-full bg-ui-bg", className)}>
      <table
        className={cn(
          "w-full border-collapse bg-ui-bg text-left text-xs",
          minWidthClassName,
          // Без структурной колонки-гаттера отступ от края живёт в первой ячейке каждой строки.
          gutterLeft && "[&_tr>*:first-child]:pl-4",
          gutterRight && "[&_tr>*:last-child]:pr-4",
        )}
      >
        <thead className="border-b border-ui-border">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const column = header.column
                const pinned = column.getIsPinned()
                const structural = staticIds.has(column.id)
                const sortable = Boolean(onSortToggle) && !structural && (sortableIds?.has(column.id) ?? true)
                const pinnable = Boolean(pinStorageKey) && !structural
                const align = rightAlignedIds?.has(column.id) ? "right" : "left"
                const isPinnedByUser = pinnedLeft.includes(column.id)
                return (
                  <th
                    key={header.id}
                    style={pinStyle(column)}
                    className={cn(
                      DATA_TABLE_TH_CLASS,
                      "group/head h-9",
                      columnClassName?.(column.id),
                      pinned && "z-20 overflow-hidden",
                      pinned && pinnedEdgeClass(column),
                    )}
                  >
                    {/* У правой колонки значки слева от подписи: край текста совпадает с ячейками. */}
                    <div className={cn("flex items-center gap-1", align === "right" && "flex-row-reverse")}>
                      {structural ? (
                        <div className="min-w-0 flex-1">
                          {header.isPlaceholder ? null : flexRender(column.columnDef.header, header.getContext())}
                        </div>
                      ) : sortable ? (
                        <button
                          type="button"
                          className={cn(
                            "inline-flex min-w-0 items-center gap-1.5 transition-colors hover:text-ui-text-primary",
                            align === "right" && "flex-row-reverse",
                          )}
                          onClick={() => onSortToggle?.(column.id)}
                        >
                          <span className="truncate">
                            {header.isPlaceholder ? null : flexRender(column.columnDef.header, header.getContext())}
                          </span>
                          <SortIcon active={sort?.id === column.id} desc={Boolean(sort?.desc)} />
                        </button>
                      ) : (
                        <span className="truncate">
                          {header.isPlaceholder ? null : flexRender(column.columnDef.header, header.getContext())}
                        </span>
                      )}
                      {pinnable ? (
                        <button
                          type="button"
                          className={cn(
                            "flex h-4 w-4 shrink-0 items-center justify-center rounded text-ui-text-faint transition-opacity hover:text-ui-text-primary",
                            "opacity-0 group-hover/head:opacity-100",
                            isPinnedByUser && "text-ui-text-secondary",
                          )}
                          aria-label={isPinnedByUser ? labels.unpinColumn : labels.pinColumn}
                          title={isPinnedByUser ? labels.unpinColumn : labels.pinColumn}
                          onClick={() => togglePin(column.id)}
                        >
                          {isPinnedByUser ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                        </button>
                      ) : null}
                    </div>
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const original = row.original
            return (
              <tr
                key={row.id}
                className={cn("group/row", rowClassName?.(original))}
                onClick={
                  onRowClick
                    ? () => {
                        if (!hasTextSelection()) onRowClick(original)
                      }
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={pinStyle(cell.column)}
                    className={cn(
                      DATA_TABLE_TD_CLASS,
                      columnClassName?.(cell.column.id),
                      rightAlignedIds?.has(cell.column.id) && "text-right",
                      pinnedTdClass(cell.column, original),
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
